import {
  Button,
  Input,
  InputGroup,
  InputLeftContent,
  Loading,
  VisuallyHidden,
} from "@mozioinc/ui";
import { useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { confirmationQuery } from "../utils/query";
import EmailSvg from "./assets/email.svg?react";
import ResendSvg from "./assets/resend-booking.svg?react";
import SuccessSvg from "./assets/success.svg?react";
import WarningSvg from "./assets/warning.svg?react";
import { Dialog } from "./dialog";
import { ResendBookingButton, useCounter } from "./resend-booking-button";
import type { TransNamespace } from "../types";

type ResendBookingDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const ResendBookingDialog: React.FC<ResendBookingDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation<TransNamespace>(
    "transfer:resend-confirmation-dialog",
  );
  const [email, setEmail] = useState("");
  const { counter, startCounter } = useCounter();

  const resendConfirmationEmailMutation = useMutation({
    mutationFn: confirmationQuery.resendConfirmationEmail,
  });

  const showLoading = resendConfirmationEmailMutation.isPending;
  const showSuccess = resendConfirmationEmailMutation.isSuccess;
  const showError = resendConfirmationEmailMutation.isError;
  const errorMessage = resendConfirmationEmailMutation.error?.message;
  const showForm = !showLoading && !showError && !showSuccess;

  const handleSend = (event: React.FormEvent) => {
    event.preventDefault();
    resendConfirmationEmailMutation.mutate(email);
  };

  const handleResend = () => {
    resendConfirmationEmailMutation.mutate(email);
  };

  const handleClose = () => {
    resendConfirmationEmailMutation.reset();
    setEmail("");
    onClose();
  };

  const titleText =
    showForm || showLoading
      ? t("Resend Confirmation Email")
      : showSuccess
        ? t("Thanks")
        : t("Something went wrong");

  const descriptionText = showForm
    ? t(
        "Tell us your email and we'll send you copies of all your upcoming bookings.",
      )
    : showSuccess
      ? t("If any reservations are found we'll send them to you.")
      : showError
        ? errorMessage || t("An error occurred while sending the email.")
        : undefined;

  return (
    <Dialog
      success={showSuccess}
      error={showError}
      open={open}
      icon={
        <>
          {showForm && <ResendSvg className="text-6xl text-primary" />}
          {showLoading && <Loading className="[&_svg]:h-16 [&_svg]:w-16" />}
          {showSuccess && (
            <div className="flex h-full w-full items-center justify-center rounded-full bg-success-300">
              <SuccessSvg className="text-8xl text-white" />
            </div>
          )}
          {showError && (
            <div className="flex h-full w-full items-center justify-center rounded-full bg-error-400">
              <WarningSvg className="text-8xl text-white" />
            </div>
          )}
        </>
      }
    >
      {(Title, Description) => (
        <>
          {showForm || showSuccess ? (
            <Title
              asChild
              className="text-2xl font-bold leading-9 tracking-normal"
            >
              <h2 className="mb-1 mt-7">{titleText}</h2>
            </Title>
          ) : (
            <VisuallyHidden asChild>
              <Title>{titleText}</Title>
            </VisuallyHidden>
          )}
          {descriptionText && (
            <Description className="mb-8 p-0 text-center text-base">
              {descriptionText}
            </Description>
          )}

          {showForm && (
            <form onSubmit={handleSend} className="w-full text-center">
              <InputGroup className="mb-4">
                <InputLeftContent>
                  <EmailSvg />
                </InputLeftContent>
                <Input
                  placeholder={t("Email address")}
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </InputGroup>
              <Button type="submit" className="w-full">
                {t("Send")}
              </Button>
              <Button variant="ghost" onClick={handleClose}>
                {t("Close")}
              </Button>
            </form>
          )}

          {(showSuccess || showError) && (
            <Button onClick={handleClose} className="mb-6 w-full">
              {t("OK")}
            </Button>
          )}

          {showSuccess && (
            <ResendBookingButton
              onButtonClick={() => {
                startCounter();
                handleResend();
              }}
              countdown={counter}
            />
          )}
        </>
      )}
    </Dialog>
  );
};
